import { useState } from 'react'
import { Body1, Link, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'

/**
 * A demand's description on an opportunity card: a few lines, the rest behind
 * "More". The detail page shows the whole text; a card is for deciding whether
 * to open it.
 */

// The import pipeline appends SharePoint bookkeeping under these markers
// (scripts/reconcile/apply_forms_mappings.py). Raw markdown, and about the
// list row rather than the work.
const SHAREPOINT_FOOTER = /\*\*(SharePoint context|Caretaker comments \(from SharePoint list\)):\*\*/

/** The description without its SharePoint footer, or '' if that was all there was. */
export function cardDescription(text: string | null | undefined): string {
  const raw = text ?? ''
  const at = raw.search(SHAREPOINT_FOOTER)
  // The footer sits under its own `---` rule; take that with it.
  const body = at === -1 ? raw : raw.slice(0, at).replace(/\s*---\s*$/, '')
  return body.trim()
}

// About three lines on the narrowest card. The layout is not measured (jsdom
// cannot), so one number decides both where to cut and whether to offer More.
const PREVIEW_CHARS = 200

function preview(text: string): string {
  const cut = text.slice(0, PREVIEW_CHARS)
  const atWord = cut.lastIndexOf(' ')
  return `${(atWord > PREVIEW_CHARS / 2 ? cut.slice(0, atWord) : cut).trimEnd()}…`
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalXXS,
  },
  text: {
    margin: 0,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  clamped: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  toggle: {
    fontSize: tokens.fontSizeBase200,
  },
})

export function OpportunityDescription({ text }: { text: string | null | undefined }) {
  const styles = useStyles()
  const [expanded, setExpanded] = useState(false)
  const body = cardDescription(text)
  if (!body) return null
  const long = body.length > PREVIEW_CHARS
  return (
    <div className={styles.root}>
      {/* Clamp only what More can reopen: a short text that still wraps past
          three lines on a narrow card would otherwise be cut for good. */}
      <Body1 className={mergeClasses(styles.text, long && !expanded && styles.clamped)}>
        {long && !expanded ? preview(body) : body}
      </Body1>
      {long && (
        <Link
          as="button"
          className={styles.toggle}
          aria-expanded={expanded}
          onClick={(e) => {
            // The browse card is itself a link; this must not open it.
            e.stopPropagation()
            setExpanded((v) => !v)
          }}
        >
          {expanded ? 'Less' : 'More'}
        </Link>
      )}
    </div>
  )
}
